import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import DonationQuickAction from '../../components/ui/DonationQuickAction';
import GivingSummaryCard from './components/GivingSummaryCard';
import DonationHistoryTable from './components/DonationHistoryTable';
import ImpactVisualization from './components/ImpactVisualization';
import QuickActionPanel from './components/QuickActionPanel';
import GamificationPanel from './components/GamificationPanel';
import PersonalizedRecommendations from './components/PersonalizedRecommendations';

const UserDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [timeRange, setTimeRange] = useState('year');

  // Mock data for giving summary
  const summaryData = [
    {
      title: "Total Donated",
      value: "$12,450",
      subtitle: "This year",
      icon: "DollarSign",
      trend: { type: "up", value: "+15%" },
      color: "primary"
    },
    {
      title: "Charities Supported",
      value: "23",
      subtitle: "Organizations",
      icon: "Heart",
      trend: { type: "up", value: "+3" },
      color: "success"
    },
    {
      title: "Lives Impacted",
      value: "1,247",
      subtitle: "People helped",
      icon: "Users",
      trend: { type: "up", value: "+8%" },
      color: "secondary"
    },
    {
      title: "Tax Deduction",
      value: "$11,205",
      subtitle: "Eligible amount",
      icon: "FileText",
      color: "warning"
    }
  ];

  // Mock donation history
  const donationHistory = [
    {
      id: 1,
      charity: "Clean Water Initiative",
      category: "Water & Sanitation",
      amount: "250.00",
      date: "2024-10-05",
      status: "completed"
    },
    {
      id: 2,
      charity: "Education for All",
      category: "Education",
      amount: "150.00",
      date: "2024-09-28",
      status: "completed"
    },
    {
      id: 3,
      charity: "Food Bank Network",
      category: "Hunger Relief",
      amount: "100.00",
      date: "2024-09-15",
      status: "completed"
    },
    {
      id: 4,
      charity: "Animal Rescue Foundation",
      category: "Animal Welfare",
      amount: "75.00",
      date: "2024-09-10",
      status: "pending"
    },
    {
      id: 5,
      charity: "Homeless Shelter Support",
      category: "Housing",
      amount: "200.00",
      date: "2024-08-30",
      status: "completed"
    }
  ];

  // Mock monthly giving data for charts
  const monthlyData = [
    { month: 'Jan', amount: 850 },
    { month: 'Feb', amount: 920 },
    { month: 'Mar', amount: 1100 },
    { month: 'Apr', amount: 980 },
    { month: 'May', amount: 1250 },
    { month: 'Jun', amount: 1150 },
    { month: 'Jul', amount: 1300 },
    { month: 'Aug', amount: 1180 },
    { month: 'Sep', amount: 1420 },
    { month: 'Oct', amount: 1350 }
  ];

  // Mock category distribution data
  const categoryData = [
    { name: 'Education', value: 3200 },
    { name: 'Healthcare', value: 2800 },
    { name: 'Environment', value: 2100 },
    { name: 'Hunger Relief', value: 1950 },
    { name: 'Animal Welfare', value: 1400 }
  ];

  // Mock impact stories
  const impactStories = [
    {
      id: 1,
      title: "Clean water access for 50 families",
      description: "Your donation helped install a new water pump in rural Kenya, providing clean drinking water to 50 families in the village.",
      charity: "Clean Water Initiative",
      date: "Oct 5",
      icon: "Droplets"
    },
    {
      id: 2,
      title: "School supplies for 25 children",
      description: "Your contribution provided essential school supplies including books, pencils, and backpacks for 25 underprivileged children.",
      charity: "Education for All",
      date: "Sep 28",
      icon: "BookOpen"
    },
    {
      id: 3,
      title: "Meals for 200 people",
      description: "Your donation helped serve 200 nutritious meals to families in need at the local food bank.",
      charity: "Food Bank Network",
      date: "Sep 15",
      icon: "Utensils"
    }
  ];

  // Mock favorite charities
  const favoriteCharities = [
    {
      id: 1,
      name: "Clean Water Initiative",
      category: "Water & Sanitation"
    },
    {
      id: 2,
      name: "Education for All",
      category: "Education"
    },
    {
      id: 3,
      name: "Animal Rescue Foundation",
      category: "Animal Welfare"
    }
  ];

  // Mock recurring donations
  const recurringDonations = [
    {
      id: 1,
      charity: "Monthly Food Bank",
      amount: "50.00",
      frequency: "Monthly",
      status: "active"
    },
    {
      id: 2,
      charity: "Education Support",
      amount: "25.00",
      frequency: "Monthly",
      status: "active"
    }
  ];

  // Mock achievements
  const achievements = [
    {
      id: 1,
      title: "First Donation",
      description: "Made your first charitable donation",
      icon: "Gift",
      unlocked: true,
      unlockedDate: "2024-01-15",
      points: 100
    },
    {
      id: 2,
      title: "Generous Giver",
      description: "Donated over $1,000 in a single year",
      icon: "Award",
      unlocked: true,
      unlockedDate: "2024-06-20",
      points: 500
    },
    {
      id: 3,
      title: "Streak Master",
      description: "Maintained a 30-day giving streak",
      icon: "Flame",
      unlocked: true,
      unlockedDate: "2024-09-10",
      points: 300
    },
    {
      id: 4,
      title: "Community Builder",
      description: "Support 10 different charities",
      icon: "Users",
      unlocked: false,
      progress: 7,
      target: 10,
      points: 250
    },
    {
      id: 5,
      title: "Impact Champion",
      description: "Help 1,000 people through donations",
      icon: "Target",
      unlocked: true,
      unlockedDate: "2024-08-15",
      points: 750
    },
    {
      id: 6,
      title: "Monthly Supporter",
      description: "Set up 5 recurring donations",
      icon: "RefreshCw",
      unlocked: false,
      progress: 2,
      target: 5,
      points: 400
    }
  ];

  // Mock recommendations
  const recommendations = [
    {
      id: 1,
      name: "Ocean Conservation Society",
      description: "Protecting marine ecosystems and endangered species through research, education, and conservation efforts worldwide.",
      category: "Environment",
      location: "Global",
      rating: 4.8,
      reason: "category_match",
      matchScore: 92,
      impactPreview: "Your $50 donation could help protect 100 square meters of coral reef habitat."
    },
    {
      id: 2,
      name: "Local Youth Mentorship",
      description: "Providing mentorship and educational support to at-risk youth in your community through after-school programs.",
      category: "Education",
      location: "San Francisco, CA",
      rating: 4.9,
      reason: "location",
      matchScore: 88,
      impactPreview: "Your donation could provide 10 hours of one-on-one mentoring for a local student."
    },
    {
      id: 3,
      name: "Global Health Initiative",
      description: "Delivering essential healthcare services and medical supplies to underserved communities in developing countries.",
      category: "Healthcare",
      location: "International",
      rating: 4.7,
      reason: "similar_donors",
      matchScore: 85,
      impactPreview: "Your contribution could provide vaccines for 20 children in rural communities."
    }
  ];

  // Mock user preferences
  const userPreferences = {
    categories: ["Education", "Environment", "Healthcare"],
    averageDonation: 125,
    frequency: "Monthly"
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'BarChart3' },
    { id: 'history', label: 'History', icon: 'History' },
    { id: 'impact', label: 'Impact', icon: 'Target' },
    { id: 'achievements', label: 'Achievements', icon: 'Trophy' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Dashboard - Share Good</title>
        <meta name="description" content="Manage your charitable giving portfolio, track impact, and monitor donation history" />
      </Helmet>
      <Header />
      <main className="max-w-7xl mx-auto px-4 lg:px-6 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">Your Giving Dashboard</h1>
          <p className="text-muted-foreground">
            Track your charitable impact and manage your giving portfolio
          </p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {summaryData?.map((data, index) => (
            <GivingSummaryCard key={index} {...data} />
          ))}
        </div>

        {/* Tab Navigation */}
        <div className="mb-8">
          <div className="border-b border-border">
            <nav className="flex space-x-8 overflow-x-auto">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-micro ${
                    activeTab === tab?.id
                      ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                  }`}
                >
                  <span>{tab?.label}</span>
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <div className="space-y-8">
          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2 space-y-8">
                <QuickActionPanel 
                  favoriteCharities={favoriteCharities}
                  recurringDonations={recurringDonations}
                />
                <DonationHistoryTable donations={donationHistory?.slice(0, 5)} />
              </div>
              <div className="space-y-8">
                <PersonalizedRecommendations 
                  recommendations={recommendations}
                  userPreferences={userPreferences}
                />
              </div>
            </div>
          )}

          {activeTab === 'history' && (
            <DonationHistoryTable donations={donationHistory} />
          )}

          {activeTab === 'impact' && (
            <ImpactVisualization 
              monthlyData={monthlyData}
              categoryData={categoryData}
              impactStories={impactStories}
            />
          )}

          {activeTab === 'achievements' && (
            <GamificationPanel 
              achievements={achievements}
              givingStreak={42}
              leaderboardPosition={127}
              totalPoints={2350}
            />
          )}
        </div>
      </main>
      <DonationQuickAction />
    </div>
  );
};

export default UserDashboard;